/**
 * CTB plot style table parser (docs/opencadstudio-parity/02-plot-pdf-ctb.md
 * task 2.2) — TS port of the OCS plot_style.rs container layout.
 *
 * Container: a fixed text header ending with the "pmzlibcodec" magic, then
 * three little-endian u32 fields (adler32 checksum, inflated size, deflated
 * size) and a zlib stream holding the plot-style grammar text. Loading is
 * TOLERANT: checksum/length mismatches become load_warnings, not failures;
 * a legacy raw-deflate fallback and a Windows-1252 decode fallback cover
 * producer variants. STB tables parse (isStb) but named-style resolution is
 * rejected — the same boundary OCS draws.
 */

import { inflate, inflateRaw } from 'pako';
import { ACI_TABLE } from './aciColors';
import type { PlotStyleEntry, PlotStyleTable } from './types';

/** Header magic terminating the fixed text header (48 bytes in v2 files:
 *  "PIAFILEVERSION_2.0,CTBVER1,compress\r\npmzlibcodec"). */
const ZLIB_MAGIC = 'pmzlibcodec';
/** CTB v2 header stamp inside the first line. */
const CTB_STAMP = 'CTBVER1';
const STB_STAMP = 'STBVER1';
/** OCS MAX_INFLATED_BYTES — decompression bomb guard. */
const MAX_INFLATED_BYTES = 64 * 1024 * 1024;

/** Packed color sentinel = "use object color". */
const OBJECT_COLOR_SENTINELS = new Set<number>([-1, -1006632961, -1056964608]);

/**
 * Standard pen weight table in mm (index 0 = 0.00 hairline). Real files
 * usually carry their own custom_lineweight_table; this is the fallback.
 */
export const LW_TABLE: number[] = [
    0.00, 0.05, 0.09, 0.13, 0.15, 0.18, 0.20, 0.25, 0.30, 0.35,
    0.40, 0.50, 0.53, 0.60, 0.70, 0.80, 0.90, 1.00, 1.06, 1.20,
    1.40, 1.58, 2.00, 2.11,
];

interface RawEntry {
    [key: string]: string;
}

/**
 * Tolerant key=value grammar reader. Handles `key=value`, `key="value"`,
 * numbered block openers (`0{`, `12{`), named section openers
 * (`plot_style{`) and closers — line by line, so values containing braces
 * or extra '=' signs survive (first '=' splits the pair, quotes strip).
 */
function parseGrammar(text: string): { root: RawEntry; sections: Map<string, Map<number, RawEntry>> } {
    const root: RawEntry = {};
    const sections = new Map<string, Map<number, RawEntry>>();
    type Frame = { index: number | null; entries: RawEntry; section: string | null };
    const stack: Frame[] = [{ index: null, entries: root, section: null }];

    const register = (frame: Frame) => {
        if (frame.index !== null && frame.section) {
            let bucket = sections.get(frame.section);
            if (!bucket) { bucket = new Map(); sections.set(frame.section, bucket); }
            bucket.set(frame.index, frame.entries);
        }
    };

    for (const rawLine of text.replace(/\r/g, '').split('\n')) {
        let line = rawLine.trim();
        while (line.length > 0) {
            if (line.startsWith('}')) {
                if (stack.length > 1) register(stack.pop()!);
                line = line.slice(1).trim();
                continue;
            }
            if (line.startsWith('{')) {
                // Anonymous block (STB named styles land here tolerantly).
                stack.push({ index: null, entries: {}, section: stack[stack.length - 1].section });
                line = line.slice(1).trim();
                continue;
            }
            const braceAt = line.indexOf('{');
            const eqAt = line.indexOf('=');
            if (braceAt >= 0 && (eqAt < 0 || braceAt < eqAt)) {
                // `<word>{` — numbered style block (`3{`) or named section.
                const word = line.slice(0, braceAt).trim();
                const index = /^-?\d+$/.test(word) ? parseInt(word, 10) : null;
                stack.push({
                    index,
                    entries: {},
                    section: index === null ? word : stack[stack.length - 1].section,
                });
                line = line.slice(braceAt + 1).trim();
                continue;
            }
            if (eqAt > 0) {
                const key = line.slice(0, eqAt).trim();
                const value = line.slice(eqAt + 1).trim().replace(/^"(.*)"$/s, '$1');
                stack[stack.length - 1].entries[key] = value;
            }
            break; // pair consumed or unrecognized line — next line
        }
    }
    while (stack.length > 1) register(stack.pop()!);
    return { root, sections };
}

/** adler32 over the inflated text — matches the header checksum field. */
function adler32(bytes: Uint8Array): number {
    let a = 1, b = 0;
    for (let i = 0; i < bytes.length; i++) {
        a = (a + bytes[i]) % 65521;
        b = (b + a) % 65521;
    }
    return ((b << 16) | a) >>> 0;
}

function decodeText(bytes: Uint8Array): string {
    // UTF-8 strict first; producer variants fall back to Windows-1252.
    try {
        return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    } catch {
        try {
            return new TextDecoder('windows-1252').decode(bytes);
        } catch {
            return new TextDecoder('utf-8').decode(bytes);
        }
    }
}

function num(v: string | undefined): number | null {
    if (v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

function bool(v: string | undefined): boolean {
    return (v ?? '').toUpperCase() === 'TRUE';
}

/** Parse a packed color value; null sentinel → null (use object color). */
function parsePackedColor(v: string | undefined): number | null {
    const n = num(v);
    if (n === null) return null;
    if (OBJECT_COLOR_SENTINELS.has(Math.trunc(n))) return null;
    return Math.trunc(n);
}

function entryFromRaw(raw: RawEntry, index: number): PlotStyleEntry {
    const color = parsePackedColor(raw.color);
    const modeColor = parsePackedColor(raw.mode_color);
    // OCS: mode_color wins over color when it compares greater.
    const resolvedColor = modeColor !== null && (color === null || modeColor > color)
        ? modeColor
        : color;
    const screening = Math.min(100, Math.max(0, num(raw.screen) ?? 100));
    return {
        name: raw.name ?? `Style ${index}`,
        localizedName: raw.localized_name || undefined,
        description: raw.description || undefined,
        screening,
        lineweight: num(raw.lineweight),
        color: resolvedColor,
        colorPolicy: num(raw.color_policy) ?? 0,
        physicalPenNumber: num(raw.physical_pen_number) ?? 0,
        virtualPenNumber: num(raw.virtual_pen_number) ?? 0,
        linepatternSize: num(raw.linepattern_size) ?? 0.5,
        linetype: num(raw.linetype) ?? 0,
        adaptiveLinetype: raw.adaptive_linetype ? bool(raw.adaptive_linetype) : true,
        fillStyle: num(raw.fill_style) ?? 0,
        endStyle: num(raw.end_style) ?? 0,
        joinStyle: num(raw.join_style) ?? 0,
    };
}

/** Decode "0" → 0mm, "5" → 0.05mm (hundredths), "0.25" → 0.25mm. */
function parseLineweightValue(v: string): number {
    const n = Number(v);
    if (!Number.isFinite(n)) return 0;
    return v.includes('.') ? Math.max(0, n) : Math.max(0, n / 100);
}

/** Parse a .ctb/.stb byte container into a PlotStyleTable. */
export function parseCtb(bytes: Uint8Array, name: string): PlotStyleTable {
    const warnings: string[] = [];
    const headerText = new TextDecoder('latin1').decode(bytes.subarray(0, 256));
    const isStb = headerText.includes(STB_STAMP);

    // Locate the zlib payload: the magic, then 3 × u32 LE fields.
    const magicAt = headerText.indexOf(ZLIB_MAGIC);
    if (magicAt < 0) {
        warnings.push('pmzlibcodec magic not found — not a compressed plot style table.');
        return { name, isStb, scaleFactor: 1, applyFactor: false, aciEntries: new Array(256).fill(null), lineweights: [...LW_TABLE], loadWarnings: warnings };
    }
    const fieldsAt = magicAt + ZLIB_MAGIC.length;
    if (bytes.length < fieldsAt + 12) {
        warnings.push('Container truncated before the size fields.');
        return { name, isStb, scaleFactor: 1, applyFactor: false, aciEntries: new Array(256).fill(null), lineweights: [...LW_TABLE], loadWarnings: warnings };
    }
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const checksum = view.getUint32(fieldsAt, true);
    const inflatedLen = view.getUint32(fieldsAt + 4, true);
    const deflatedLen = view.getUint32(fieldsAt + 8, true);
    const zlibStart = fieldsAt + 12;
    const zlibEnd = deflatedLen > 0 ? Math.min(bytes.length, zlibStart + deflatedLen) : bytes.length;

    // Inflate (zlib with raw-deflate legacy fallback), guarded by size.
    let text = '';
    let inflated: Uint8Array | null = null;
    try {
        inflated = inflate(bytes.subarray(zlibStart, zlibEnd));
    } catch {
        try {
            inflated = inflateRaw(bytes.subarray(zlibStart, zlibEnd));
            warnings.push('Legacy raw-deflate stream accepted.');
        } catch (e) {
            warnings.push(`Decompression failed: ${e instanceof Error ? e.message : 'unknown error'}.`);
        }
    }
    if (inflated) {
        if (inflated.length > MAX_INFLATED_BYTES) {
            warnings.push(`Inflated size ${inflated.length} exceeds the 64 MiB guard — table truncated.`);
            inflated = inflated.subarray(0, MAX_INFLATED_BYTES);
        }
        if (inflatedLen > 0 && inflated.length !== inflatedLen) {
            warnings.push(`Inflated length mismatch (header ${inflatedLen}, actual ${inflated.length}).`);
        }
        const actualChecksum = adler32(inflated);
        if (checksum !== 0 && actualChecksum !== checksum) {
            warnings.push(`adler32 mismatch (header ${checksum}, actual ${actualChecksum}).`);
        }
        text = decodeText(inflated);
    }

    // Grammar pass.
    const { root, sections } = parseGrammar(text);
    const plotStyles = sections.get('plot_style') ?? new Map<number, RawEntry>();
    const customWeights = sections.get('custom_lineweight_table');
    const lineweights = [...LW_TABLE];
    if (customWeights && customWeights.size > 0) {
        customWeights.forEach((raw, idx) => {
            const v = raw.lineweight ?? raw['weight'];
            if (v !== undefined) lineweights[idx] = parseLineweightValue(v);
        });
    }

    const aciEntries: (PlotStyleEntry | null)[] = new Array(256).fill(null);
    plotStyles.forEach((raw, idx) => {
        if (idx >= 0 && idx < 256) aciEntries[idx] = entryFromRaw(raw, idx);
    });

    return {
        name,
        isStb,
        scaleFactor: num(root.scale_factor) ?? 1,
        applyFactor: bool(root.apply_factor),
        aciEntries,
        lineweights,
        loadWarnings: warnings,
    };
}

/** Read a File/Blob (or raw bytes) into the parser. */
export async function loadCtbBlob(source: Blob | Uint8Array, name: string): Promise<PlotStyleTable> {
    const bytes = source instanceof Uint8Array ? source : new Uint8Array(await source.arrayBuffer());
    return parseCtb(bytes, name);
}

/** Packed 0xRRGGBB → '#rrggbb'. */
export function packedToHex(packed: number): string {
    const r = (packed >> 16) & 0xff, g = (packed >> 8) & 0xff, b = packed & 0xff;
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

/** Resolve the style color for an ACI; null = object color passes through. */
export function resolveColor(table: PlotStyleTable, aci: number): string | null {
    if (table.isStb) return null;
    const entry = table.aciEntries[aci] ?? table.aciEntries[0];
    return entry?.color != null ? packedToHex(entry.color) : null;
}

/**
 * Resolve the physical lineweight in mm for an ACI; null = use the object
 * lineweight (custom table index out of range or STB).
 */
export function resolveLineweight(table: PlotStyleTable, aci: number): number | null {
    if (table.isStb) return null;
    const entry = table.aciEntries[aci] ?? table.aciEntries[0];
    if (!entry || entry.lineweight === null || entry.lineweight < 0) return null;
    const idx = Math.floor(entry.lineweight);
    const mm = table.lineweights[idx];
    return typeof mm === 'number' ? mm : null;
}

/** Resolve screening (0-1 multiplier) for an ACI. */
export function resolveScreening(table: PlotStyleTable, aci: number): number {
    if (table.isStb) return 1;
    const entry = table.aciEntries[aci] ?? table.aciEntries[0];
    return entry ? entry.screening / 100 : 1;
}

/** Luminance per the OCS/broadcast formula (0.299/0.587/0.114). */
function luminance([r, g, b]: [number, number, number]): number {
    return 0.299 * r + 0.587 * g + 0.114 * b;
}

function grayPacked(y: number): number {
    const v = Math.max(0, Math.min(255, Math.round(y)));
    return (v << 16) | (v << 8) | v;
}

function builtInTable(name: string, map: (rgb: [number, number, number]) => number): PlotStyleTable {
    const aciEntries: (PlotStyleEntry | null)[] = new Array(256).fill(null);
    for (let aci = 1; aci <= 255; aci++) {
        aciEntries[aci] = {
            ...blankEntry(aci),
            color: map(ACI_TABLE[aci]),
        };
    }
    return { name, isStb: false, scaleFactor: 1, applyFactor: false, aciEntries, lineweights: [...LW_TABLE], loadWarnings: [] };
}

function blankEntry(index: number): PlotStyleEntry {
    return {
        name: `Style ${index}`,
        screening: 100,
        lineweight: null,
        color: null,
        colorPolicy: 0,
        physicalPenNumber: 0,
        virtualPenNumber: 0,
        linepatternSize: 0.5,
        linetype: 0,
        adaptiveLinetype: true,
        fillStyle: 0,
        endStyle: 0,
        joinStyle: 0,
    };
}

/** Built-in monochrome.ctb equivalent: every pen plots black. */
export function monochromeTable(): PlotStyleTable {
    return builtInTable('monochrome.ctb', () => 0x000000);
}

/** Built-in grayscale.ctb equivalent: per-ACI broadcast luminance. */
export function grayscaleTable(): PlotStyleTable {
    return builtInTable('grayscale.ctb', (rgb) => grayPacked(luminance(rgb)));
}
